import { useOnClickOutside } from '@/hooks';
import useScrollIntoView from '@/hooks/useScrollIntoView';
import { DropdownActionBarProps } from '@/storybook/components/FormCollection/components/DropdownActionBar/DropdownActionBar';
import DropdownButton, {
  DropdownButtonSize,
} from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownLabel from '@/storybook/components/FormCollection/components/DropdownLabel/DropdownLabel';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import VirtualizedRows from '@/storybook/components/FormCollection/components/VirtualizedRows/VirtualizedRows';
import { DropdownPosition, handleDropdownListPosition } from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { buildAvailableValueSet, formatActiveTotalBadge, getActiveTotalCount } from '@/utils/filter/cascadingFilters';
import cx from 'classnames';
import { JSX, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../Input.module.css';
import { useMultiSelect } from './hooks/useMultiSelect';

export type { GroupedOptionList } from './hooks/useMultiSelectOptions';

export type MultiSelectProps<T = string> = {
  'label'?: ReactNode;
  'tooltip'?: ReactNode;
  'options'?: SelectItem<T>[];
  'unfilteredOptions'?: SelectItem<T>[];
  'searchable'?: boolean;
  'isLoading'?: boolean;
  'countLoading'?: boolean;
  'value'?: Array<T>;
  'onChange': (selectedValue?: Array<T>) => void;
  'onClear'?: VoidFunction;
  'placeholder'?: string | JSX.Element;
  'disabled'?: boolean;
  'clearable'?: boolean;
  'adobeEventTrackFunctions'?: VoidFunction;
  'onDropdownToggle'?: (isOpen: boolean) => void;
  'alignment'?: 'vertical' | 'horizontal';
  'data-testid'?: string;
  'dropdownLabelTestId'?: string;
  'dropdownButtonTestId'?: string;
  'dropdownListTestId'?: string;
  'dropdownPosition'?: DropdownPosition;
  'customSelectItemFn'?: (value: T, options: SelectItem<T>[]) => SelectItem<T> | undefined;
  'size'?: keyof typeof DropdownButtonSize;
  'className'?: string;
  'childClassName'?: string;
  'actionBarProps'?: DropdownActionBarProps;
};

export default function MultiSelect<T>({
  label,
  tooltip,
  options,
  unfilteredOptions,
  isLoading = false,
  countLoading = false,
  value = [],
  onChange,
  onClear,
  placeholder,
  disabled = false,
  clearable = true,
  searchable = false,
  adobeEventTrackFunctions,
  onDropdownToggle,
  alignment = 'vertical',
  'data-testid': dataTestId = 'singleSelectDropdown',
  dropdownLabelTestId,
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  customSelectItemFn,
  size = 'L',
  className,
  childClassName,
  actionBarProps,
}: MultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');
  const [searchInput, setSearchInput] = useState<string | undefined>();
  const [memoizedValueList, setMemoizedValueList] = useState<Array<T>>([]);

  const { t } = useTranslation();

  const dropdownRef = useOnClickOutside(() => {
    if (isOpen) onDropdownToggle?.(false);
    setIsOpen(false);
  });
  const dropdownButtonRef = useRef<HTMLDivElement>(null);

  useScrollIntoView({ elementRef: dropdownRef, isVisible: isOpen });

  const { searchResults, dropdownButtonValue, virtualizedList, parentRef, renderVirtualRow } = useMultiSelect<T>({
    options,
    unfilteredOptions,
    value,
    memoizedValueList,
    searchInput,
    disabled,
    noCategoryLabel: t('components.dropdownPanel.noCategory'),
    onChange,
    customSelectItemFn,
    size,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    const newState = !isOpen;
    setIsOpen(newState);
    onDropdownToggle?.(newState);
    if (newState) {
      handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
      setMemoizedValueList(value);
      setSearchInput(undefined);
    }
  }, [disabled, isOpen, dropdownPosition, dropdownRef, value, onDropdownToggle]);

  const handleOnClearValue = useCallback(() => {
    if (disabled || !clearable) return;
    if (onClear) onClear();
    if (!onClear) onChange([]);
  }, [onClear, disabled, clearable, onChange]);

  // Cascading filters: when options carry disabled flags, show `{active}/{total}`.
  const countLabel = useMemo(() => {
    const hasCascadingState = options?.some(option => option.disabled != null) ?? false;
    if (!hasCascadingState) return undefined;
    const availableValues = buildAvailableValueSet(options?.filter(option => option.disabled !== true));
    return formatActiveTotalBadge(getActiveTotalCount(value, availableValues)) ?? undefined;
  }, [options, value]);

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.inputContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
    >
      <DropdownLabel label={label} tooltip={tooltip} data-testid={dropdownLabelTestId} />
      <section className={styles.dropdownContainer}>
        <DropdownButton
          ref={dropdownButtonRef}
          type="multiSelect"
          size={size}
          placeholder={placeholder}
          numOfValue={value.length}
          active={isOpen}
          onClick={!disabled ? handleToggleDropdown : undefined}
          onClear={handleOnClearValue}
          clearable={clearable}
          disabled={disabled}
          countLabel={countLabel}
          countLoading={countLoading}
          className={childClassName}
          data-testid={dropdownButtonTestId}
        >
          {dropdownButtonValue}
        </DropdownButton>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={disabled}
          position={dropdownListPosition}
          isSearchable={searchable}
          searchValue={searchInput}
          onSearchValueChange={setSearchInput}
          numberOfSearchResults={searchResults.length}
          data-testid={dropdownListTestId}
          listRef={parentRef}
          dropdownTriggerRef={dropdownButtonRef}
          optionsCount={options?.length ?? 0}
          actionBarProps={actionBarProps}
        >
          <VirtualizedRows virtualization={virtualizedList} renderRow={renderVirtualRow} />
        </DropdownList>
      </section>
    </div>
  );
}
